/**
 * Buy / Restore buttons shared by the paywall and checkout. Inside the Android
 * app they go through Google Play; in a browser they point to the app.
 */
import { ApiError } from '../api/client.js';
import { refreshCredits } from '../state/credits.js';
import { toast } from '../ui/toast.js';
import { PLAY_STORE_URL, buyWithPlay, playBilling, restorePlayPurchases } from './play.js';

const num = (n: number): string => n.toLocaleString('en-US');

let busy = false;

/** Returns true when credits were added (the caller can leave the page). */
export async function purchase(productId: string): Promise<boolean> {
  if (busy) return false;
  if (!(await playBilling())) {
    toast('Purchases are available in the Loomia app on Google Play.');
    window.open(PLAY_STORE_URL, '_blank', 'noopener');
    return false;
  }
  busy = true;
  try {
    const res = await buyWithPlay(productId);
    if (res.status === 'cancelled') return false;
    void refreshCredits();
    if (res.status === 'pending') {
      toast('Payment pending — your credits arrive as soon as Google Play confirms it.');
      return false;
    }
    toast(`+${num(res.credits)} credits added. Thank you!`);
    return true;
  } catch (err) {
    toast(err instanceof ApiError ? err.message : 'The purchase could not be completed. You were not charged twice — try Restore.');
    return false;
  } finally {
    busy = false;
  }
}

export async function restore(): Promise<void> {
  try {
    const added = await restorePlayPurchases();
    if (added === null) {
      toast('Restore works in the Loomia app on Google Play.');
      return;
    }
    void refreshCredits();
    toast(added > 0 ? `Restored ${num(added)} credits.` : 'Everything is already on your account.');
  } catch {
    toast('Could not reach Google Play. Please try again.');
  }
}

